import { apiService, organizationIdHeader } from "@/services/api.service";

export interface EntityFileHost {
	entityType: string;
	organizationId: string;
	entityId: string;
}

export function entityFileDownloadPath(
	host: EntityFileHost,
	fileId: string,
): string {
	return `/files/${host.entityType}/${host.organizationId}/${host.entityId}/${fileId}`;
}

export function contentDispositionHeader(headers: {
	get?: (name: string) => unknown;
	[key: string]: unknown;
}): string | null {
	const direct = headers["content-disposition"];
	if (typeof direct === "string" && direct.length > 0) return direct;
	if (typeof headers.get === "function") {
		const value = headers.get("content-disposition");
		if (typeof value === "string" && value.length > 0) return value;
	}
	return null;
}

/** Filename from a `Content-Disposition` header (`filename*` then `filename`). */
export function fileNameFromContentDisposition(
	header: string | null | undefined,
): string | null {
	if (!header) return null;
	const encoded = /filename\*\s*=\s*(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
	if (encoded?.[1]) {
		const raw = encoded[1].trim().replace(/^"|"$/g, "");
		try {
			const decoded = decodeURIComponent(raw);
			if (decoded.length > 0) return decoded;
		} catch {
			if (raw.length > 0) return raw;
		}
	}
	const plain =
		/filename\s*=\s*"([^"]+)"/i.exec(header) ??
		/filename\s*=\s*([^;]+)/i.exec(header);
	const name = plain?.[1]?.trim();
	return name && name.length > 0 ? name : null;
}

/**
 * Bytes for a dynamic-form file already saved on a job, ticket, asset, or record.
 * The download name is `FileInfo.Name` from `Content-Disposition`.
 */
export async function fetchEntityFormFile(
	host: EntityFileHost,
	fileId: string,
): Promise<{ blob: Blob; fileName: string | null }> {
	const response = await apiService.getResponse<Blob>(
		entityFileDownloadPath(host, fileId),
		{
			responseType: "blob",
			headers: {
				Accept: "*/*",
				...organizationIdHeader(host.organizationId),
			},
		},
	);
	const blob = response.data;
	if (!(blob instanceof Blob)) {
		throw new Error("File download did not return a file.");
	}
	return {
		blob,
		fileName: fileNameFromContentDisposition(
			contentDispositionHeader(response.headers),
		),
	};
}
